// pages/CourierOrdersPage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api'; // Use the configured api instance instead of axios
import './CourierOrdersPage.css';

function CourierOrdersPage() {
  const [courierRequests, setCourierRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCourierRequests();
  }, []);

  const fetchCourierRequests = async () => {
    try {
      setLoading(true);
      // Use the configured api instance
      const response = await api.get('/courier-requests');
      
      // Check if response.data is an array
      const data = Array.isArray(response.data) ? response.data : [];
      
      console.log('API Response:', response.data); // Debug logging
      
      setCourierRequests(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch courier requests');
      console.error('Error fetching courier requests:', err);
      console.error('Error details:', err.response?.data); // More detailed error logging
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      // Use the configured api instance
      const response = await api.put(`/courier-requests/${requestId}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        // Update the request in state
        setCourierRequests(prevRequests => 
          prevRequests.map(request => 
            request._id === requestId 
              ? { ...request, status: newStatus }
              : request
          )
        );
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING': '#f39c12',
      'COURIER_ASSIGNED': '#3498db',
      'PICKED_UP': '#16a085',
      'DELIVERED': '#27ae60',
      'CANCELLED': '#e74c3c'
    };
    return colors[status] || '#95a5a6';
  };

  // Ensure courierRequests is always an array before filtering
  const filteredRequests = Array.isArray(courierRequests) 
    ? courierRequests.filter(request => {
        const matchesStatus = statusFilter === 'ALL' || request.status === statusFilter;
        const matchesSearch = request.dispensaryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              request.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              request.customerName.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
      })
    : [];

  if (loading) {
    return (
      <div className="courier-requests-container">
        <div className="loading">Loading courier requests...</div>
      </div>
    );
  }

  return (
    <div className="courier-requests-container">
      <div className="courier-requests-header">
        <h1>Courier Requests</h1>
        <button onClick={fetchCourierRequests} className="refresh-btn">
          Refresh
        </button>
      </div>

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by dispensary, order #, or customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="status-filter">
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-select"
          >
            <option value="ALL">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="COURIER_ASSIGNED">Courier Assigned</option>
            <option value="PICKED_UP">Picked Up</option>
            <option value="DELIVERED">Delivered</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {filteredRequests.length === 0 ? (
        <div className="no-requests">
          {searchTerm || statusFilter !== 'ALL' 
            ? 'No courier requests match your filters'
            : 'No courier requests found'}
        </div>
      ) : (
        <div className="requests-grid">
          {filteredRequests.map(request => (
            <div key={request._id} className="request-card">
              <div className="request-header">
                <div className="request-title">
                  <h3>{request.dispensaryName}</h3>
                  <span className="order-number">Order #{request.orderNumber}</span>
                </div>
                <div 
                  className="status-badge" 
                  style={{ backgroundColor: getStatusColor(request.status) }}
                >
                  {request.status.replace('_', ' ')}
                </div>
              </div>

              <div className="request-details">
                <div className="detail-section">
                  <h4>Dispensary Info</h4>
                  <p><strong>Address:</strong> {request.address}</p>
                  <p><strong>Contact:</strong> {request.contactPhone}</p>
                </div>

                <div className="detail-section">
                  <h4>Customer Info</h4>
                  <p><strong>Name:</strong> {request.customerName}</p>
                  <p><strong>Phone:</strong> {request.customerPhone}</p>
                  <p><strong>Delivery Address:</strong> {request.deliveryAddress}</p>
                </div>

                <div className="detail-section">
                  <h4>Order Details</h4>
                  <p><strong>Total:</strong> ${request.orderTotal}</p>
                  <p><strong>Courier Payment:</strong> ${request.courierPayment}</p>
                  <p><strong>Pickup:</strong> {request.pickupTimeOption.toUpperCase()}</p>
                  {request.requestedPickupTime && (
                    <p><strong>Requested Time:</strong> {formatDate(request.requestedPickupTime)}</p>
                  )}
                  {request.notes && (
                    <p><strong>Notes:</strong> {request.notes}</p>
                  )}
                </div>

                {request.products && request.products.length > 0 && (
                  <div className="detail-section">
                    <h4>Products</h4>
                    <ul className="products-list">
                      {request.products.map((product, index) => (
                        <li key={index}>
                          {product.name} - Qty: {product.quantity}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="detail-section">
                  <h4>Timestamps</h4>
                  <p><strong>Created:</strong> {formatDate(request.createdAt)}</p>
                  {request.assignedAt && (
                    <p><strong>Assigned:</strong> {formatDate(request.assignedAt)}</p>
                  )}
                  {request.pickedUpAt && (
                    <p><strong>Picked Up:</strong> {formatDate(request.pickedUpAt)}</p>
                  )}
                  {request.deliveredAt && (
                    <p><strong>Delivered:</strong> {formatDate(request.deliveredAt)}</p>
                  )}
                </div>
              </div>

              <div className="request-actions">
                {request.status === 'PENDING' && (
                  <button 
                    onClick={() => updateStatus(request._id, 'COURIER_ASSIGNED')}
                    className="action-btn assign-btn"
                  >
                    Assign Courier
                  </button>
                )}
                {request.status === 'COURIER_ASSIGNED' && (
                  <button 
                    onClick={() => updateStatus(request._id, 'PICKED_UP')}
                    className="action-btn pickup-btn"
                  >
                    Mark as Picked Up
                  </button>
                )}
                {request.status === 'PICKED_UP' && (
                  <button 
                    onClick={() => updateStatus(request._id, 'DELIVERED')}
                    className="action-btn deliver-btn"
                  >
                    Mark as Delivered
                  </button>
                )}
                {(request.status === 'PENDING' || request.status === 'COURIER_ASSIGNED') && (
                  <button 
                    onClick={() => updateStatus(request._id, 'CANCELLED')}
                    className="action-btn cancel-btn"
                  >
                    Cancel Request
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CourierOrdersPage;